/**
 * Shared primitives. Every value here is lifted literally from the design
 * handoff — sizes, tracking, radii and colours are not reinterpreted.
 */
import type { CSSProperties, ReactNode } from 'react';
import { border, c, mono, radius, shadow } from '../theme/tokens';

// ── Text ─────────────────────────────────────────────────────────────────────

export function Mono({
  children,
  size = 11,
  tracking = 0.1,
  color = c.ink3,
  style,
}: {
  children: ReactNode;
  size?: number;
  tracking?: number;
  color?: string;
  style?: CSSProperties;
}) {
  return (
    <span style={{ fontFamily: mono, fontSize: size, letterSpacing: `${tracking}em`, color, ...style }}>
      {children}
    </span>
  );
}

/** Mono eyebrow above a screen h1: 11.5px, tracking 0.14em. */
export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <div style={{ fontFamily: mono, fontSize: 11.5, letterSpacing: '0.14em', color: c.ink3 }}>{children}</div>
  );
}

/** Screen h1: 38px / 1.1 / −0.015em. */
export function H1({ children }: { children: ReactNode }) {
  return (
    <h1
      style={{
        margin: 0,
        fontSize: 38,
        lineHeight: 1.1,
        letterSpacing: '-0.015em',
        color: c.ink,
        textWrap: 'pretty' as CSSProperties['textWrap'],
      }}
    >
      {children}
    </h1>
  );
}

export function H3({ children, size = 18 }: { children: ReactNode; size?: number }) {
  return <h3 style={{ margin: 0, fontSize: size, lineHeight: 1.2, color: c.ink }}>{children}</h3>;
}

// ── Containers ───────────────────────────────────────────────────────────────

export function Card({
  children,
  padding = 26,
  gap = 18,
  hover = true,
  style,
}: {
  children: ReactNode;
  padding?: number | string;
  gap?: number;
  hover?: boolean;
  style?: CSSProperties;
}) {
  return (
    <div
      className={hover ? 'vp-card' : undefined}
      style={{
        background: c.surface2,
        border: border.default,
        borderRadius: radius.card,
        padding,
        display: 'flex',
        flexDirection: 'column',
        gap,
        boxShadow: shadow.card,
        minWidth: 0,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/** Card header: h3 left, mono or link right, baseline aligned. */
export function CardHead({
  title,
  right,
  titleSize = 18,
  align = 'baseline',
}: {
  title: ReactNode;
  right?: ReactNode;
  titleSize?: number;
  align?: 'baseline' | 'flex-start';
}) {
  return (
    <div style={{ display: 'flex', alignItems: align, justifyContent: 'space-between', gap: 12 }}>
      {typeof title === 'string' ? <H3 size={titleSize}>{title}</H3> : title}
      {right}
    </div>
  );
}

export function SectionRule({ title, note }: { title: string; note?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
      <h2 style={{ margin: 0, fontSize: 24, lineHeight: 1.2, color: c.ink }}>{title}</h2>
      {note ? <Mono size={11}>{note}</Mono> : null}
      <div style={{ flex: 1, height: 1, background: c.line }} />
    </div>
  );
}

// ── Pills and dots ───────────────────────────────────────────────────────────

export function Pill({
  children,
  bg,
  fg,
  size = 9.5,
  tracking = 0.08,
  borderColor,
}: {
  children: ReactNode;
  bg?: string;
  fg: string;
  size?: number;
  tracking?: number;
  borderColor?: string;
}) {
  return (
    <span
      style={{
        fontFamily: mono,
        fontSize: size,
        letterSpacing: `${tracking}em`,
        padding: '4px 9px 5px 9px',
        borderRadius: radius.pill,
        background: bg ?? 'transparent',
        color: fg,
        border: borderColor ? `1px solid ${borderColor}` : undefined,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {children}
    </span>
  );
}

export function TierPill({ tier }: { tier: 'PRO' | 'SELECT' }) {
  const fg = tier === 'PRO' ? c.orange700 : c.indigo;
  const bd = tier === 'PRO' ? 'rgba(198,111,0,0.35)' : 'rgba(9,64,116,0.35)';
  return (
    <span
      style={{
        fontFamily: mono,
        fontSize: 9.5,
        letterSpacing: '0.14em',
        color: fg,
        border: `1px solid ${bd}`,
        borderRadius: radius.pill,
        padding: '2px 8px 3px 8px',
      }}
    >
      {tier}
    </span>
  );
}

export function Dot({ color, size = 6, pulse = false }: { color: string; size?: number; pulse?: boolean }) {
  return (
    <span
      style={{
        width: size,
        height: size,
        flex: `0 0 ${size}px`,
        borderRadius: radius.pill,
        background: color,
        animation: pulse ? 'vpPulse 2.4s cubic-bezier(0.22,1,0.36,1) infinite' : undefined,
      }}
    />
  );
}

// ── Buttons ──────────────────────────────────────────────────────────────────

export function PrimaryButton({
  children,
  onClick,
  disabled,
  title,
  style,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  title?: string;
  style?: CSSProperties;
}) {
  return (
    <button
      className={disabled ? 'vp-reset' : 'vp-reset vp-primary'}
      onClick={disabled ? undefined : onClick}
      title={title}
      aria-disabled={disabled}
      style={{
        textAlign: 'center',
        padding: '11px 18px',
        borderRadius: radius.pill,
        background: disabled ? c.lineSoft : c.indigo,
        color: disabled ? c.ink3 : c.surface2,
        border: `1px solid ${disabled ? c.line : c.indigo}`,
        fontSize: 14,
        whiteSpace: 'nowrap',
        cursor: disabled ? 'not-allowed' : 'pointer',
        ...style,
      }}
    >
      {children}
    </button>
  );
}

export function OutlineButton({
  children,
  onClick,
  title,
  style,
  disabled = false,
}: {
  children: ReactNode;
  onClick?: () => void;
  title?: string;
  style?: CSSProperties;
  disabled?: boolean;
}) {
  return (
    <button
      className={disabled ? 'vp-reset' : 'vp-reset vp-outline'}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={title}
      style={{
        textAlign: 'center',
        padding: '11px 18px',
        borderRadius: radius.pill,
        border: border.default,
        color: disabled ? c.ink3 : c.ink2,
        background: disabled ? c.lineSoft : undefined,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 14,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {children}
    </button>
  );
}

/** Underlined text action — indigo, orange on hover. */
export function LinkButton({
  children,
  onClick,
  underline = true,
  size = 13,
}: {
  children: ReactNode;
  onClick?: () => void;
  underline?: boolean;
  size?: number;
}) {
  return (
    <button
      className="vp-reset vp-link"
      onClick={onClick}
      style={{
        fontSize: size,
        color: c.indigo,
        borderBottom: underline ? '1px solid rgba(9,64,116,0.30)' : undefined,
        width: 'fit-content',
      }}
    >
      {children}
    </button>
  );
}

export function GhostButton({ children, onClick }: { children: ReactNode; onClick?: () => void }) {
  return (
    <button
      className="vp-reset vp-ghost"
      onClick={onClick}
      style={{ textAlign: 'center', padding: '6px 16px', color: c.ink3, fontSize: 13 }}
    >
      {children}
    </button>
  );
}
